import { defineStore } from 'pinia';
import { DutyChangePreviewResponse, DutyChange, ObjectId } from '@/rennovation/planning-view/types/api/preview-response';
import { ref, shallowRef, computed, triggerRef } from 'vue';
import { usePreviewApi } from '@/rennovation/common/services/api/usePreviewApi';
import { useAlertStore } from './useAlertStore';
import { STORAGE_KEYS } from '@/rennovation/planning-view/constants';
import i18n from '@/plugins/i18n';

export const usePreviewStore = defineStore('preview', () => {
	const currentPreview = shallowRef<DutyChangePreviewResponse | null>(null);
	const isPanelOpen = shallowRef<boolean>(localStorage.getItem(STORAGE_KEYS.PREVIEW_PANEL_OPEN) === 'true');
	const changesMappedByDay = shallowRef<Record<string | number, any>>({});
	const previewViolationsMappedByDay = shallowRef<Record<string | number, any>>({});
	const selectedChangeLog = shallowRef<any>(null);
	const changesPanelActiveTab = shallowRef<any>(null);
	const loading = shallowRef<boolean>(false);
	const cardClicked = ref(0);
	const alertStore = useAlertStore();
	const t = (key: string) => i18n.global.t(key);

	const isActive = computed(() => currentPreview.value?.status === 'active');
	const violations = computed(() => currentPreview.value?.violations ?? []);
	const changes = computed(() => currentPreview.value?.changes ?? []);

	const affectedDrivers = computed(() => currentPreview.value?.affectedDrivers ?? []);
	const affectedDuties = computed(() => currentPreview.value?.affectedDuties ?? []);

	const previewApi = usePreviewApi();

	function setPanelOpen(value: boolean): void {
		isPanelOpen.value = value;
		localStorage.setItem(STORAGE_KEYS.PREVIEW_PANEL_OPEN, String(value));
	}

	function togglePanel(): void {
		if (!isActive.value) return;
		setPanelOpen(!isPanelOpen.value);
	}

	function groupByDay<T extends { day?: ObjectId }>(items?: T[] | null): Record<ObjectId, T[]> {
		const grouped: Record<ObjectId, T[]> = {};
		for (const item of items ?? []) {
			if (item.day != null) {
				(grouped[item.day] ??= []).push(item);
			}
		}
		return grouped;
	}

	function setPreview(preview: DutyChangePreviewResponse | null): void {
		currentPreview.value = preview;
		changesMappedByDay.value = groupByDay(preview?.changes);
		previewViolationsMappedByDay.value = groupByDay(preview?.violations);

		if (!preview || !isActive.value) {
			setPanelOpen(false);
		} else if (isActive.value) {
			setPanelOpen(true);
		}
	}

	async function loadPreview(): Promise<void> {
		loading.value = true;
		try {
			const result = await previewApi.getMyActive();
			setPreview(result ?? null);
		} catch (error: any) {
			if (error?.response?.status === 404) {
				currentPreview.value = null;
				triggerRef(currentPreview);
				return null;
			}
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
		} finally {
			loading.value = false;
		}
	}

	async function createPreview(payload: unknown): Promise<void> {
		loading.value = true;
		try {
			const result = await previewApi.createMine(payload);
			setPreview(result);
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
		} finally {
			loading.value = false;
		}
	}

	async function updatePreview(id: string, payload: { changes: DutyChange[] }): Promise<void> {
		try {
			const result = await previewApi.updateMine(id, payload);
			currentPreview.value = {
				...currentPreview.value,
				changes: result.changes,
			};
			triggerRef(currentPreview);
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
		}
	}

	async function publishPreview(): Promise<void> {
		loading.value = true;
		try {
			await previewApi.publishMine();
			currentPreview.value = null;
			setPanelOpen(false);
			changesMappedByDay.value = null;
			selectedChangeLog.value = null;
			previewViolationsMappedByDay.value = null;
			triggerRef(currentPreview);
			triggerRef(changesMappedByDay);
			triggerRef(previewViolationsMappedByDay);
			triggerRef(selectedChangeLog);
			alertStore.showAlert(t('preview_published_successfully'), 'success');
		} catch (error: any) {
			let errorMessage = error?.response?.data?.message || t('data_load_failed');
			if (i18n.te(errorMessage)) {
				errorMessage = t(errorMessage);
			}
			alertStore.showAlert(errorMessage, 'error');
		} finally {
			loading.value = false;
		}
	}

	async function cancelPreview(): Promise<void> {
		loading.value = true;
		try {
			await previewApi.cancelMine();
			currentPreview.value = null;
			setPanelOpen(false);
			changesMappedByDay.value = null;
			selectedChangeLog.value = null;
			previewViolationsMappedByDay.value = null;

			triggerRef(currentPreview);
			triggerRef(changesMappedByDay);
			triggerRef(previewViolationsMappedByDay);
			triggerRef(selectedChangeLog);

			alertStore.showAlert(t('preview_cancelled_successfully'), 'success');
			triggerRef(changesMappedByDay);
			triggerRef(previewViolationsMappedByDay);
			triggerRef(selectedChangeLog);
			alertStore.showAlert(t('preview_cancelled_successfully'), 'success');
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
		} finally {
			loading.value = false;
		}
	}

	function clearPreview(): void {
		currentPreview.value = null;
		changesMappedByDay.value = {};
		previewViolationsMappedByDay.value = {};
		selectedChangeLog.value = null;
		setPanelOpen(false);
	}

	async function undoChange(): Promise<void> {
		try {
			const res: any = await previewApi.undoChange();
			alertStore.showAlert(t(res?.message), 'success');
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
		}
	}

	async function redoChange(): Promise<void> {
		try {
			const res: any = await previewApi.redoChange();
			alertStore.showAlert(t(res?.message), 'success');
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
		}
	}

	return {
		currentPreview,
		loading,
		isActive,
		isPanelOpen,
		violations,
		changes,
		changesMappedByDay,
		previewViolationsMappedByDay,
		selectedChangeLog,
		affectedDrivers,
		affectedDuties,
		changesPanelActiveTab,
		setPreview,
		cardClicked,
		loadPreview,
		createPreview,
		publishPreview,
		cancelPreview,
		clearPreview,
		togglePanel,
		updatePreview,
		undoChange,
		redoChange,
	};
});
